import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TabView, TabPanel } from 'primereact/tabview';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import SignaturePad from 'react-signature-pad-wrapper';

import ItemNumberInput from '../components/ItemNumberInput.jsx';
import fetchApi, { fetchDownload } from '../lib/fetchApi.js';
// Lookup data (titles / level_types / moving_items) now comes from
// /api/v1/general/getData. The original JSON file at lib/data.js is
// kept around as a reference (do NOT re-import — see DIRECTIONS.md).
// import { TITLES, LEVEL_TYPES, MOVING_ITEMS } from '../lib/data.js';
import { dateToString, dateToTimeString } from '../lib/functions.js';
import { ProgressSpinner } from 'primereact/progressspinner';

/**
 * Normalise a quotation row straight from the backend into the shape
 * the form expects. The DB returns:
 *   - DATE / TIME columns as ISO-style strings ("YYYY-MM-DD", "HH:MM:SS")
 *   - DECIMAL columns as numeric strings ("35000.00")
 *   - TINYINT booleans as 0/1 integers
 *   - moving_items as a JSON-encoded MEDIUMTEXT string
 *
 * PrimeReact's <Calendar> needs a Date object, <Checkbox> wants a
 * boolean, <InputNumber> wants a number, and the moving-items tab
 * expects an array — so we coerce everything through one place.
 */
const normalizeQuotationFromServer = (q) => {
  if (!q) return null;
  const out = { ...q };

  // moving_items: string → array
  if (typeof out.moving_items === 'string') {
    try { out.moving_items = JSON.parse(out.moving_items || '[]'); }
    catch { out.moving_items = []; }
  } else if (!Array.isArray(out.moving_items)) {
    out.moving_items = [];
  }

  // DATE columns ("YYYY-MM-DD") → Date object, local midnight.
  const dateStrToDate = (s) => {
    if (!s) return null;
    if (s instanceof Date) return s;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  };
  out.moving_date     = dateStrToDate(out.moving_date);
  out.provide_box_date = dateStrToDate(out.provide_box_date);
  out.payment_date    = dateStrToDate(out.payment_date);

  // TIME column ("HH:MM:SS" or "HH:MM") → Date today, with that time of day.
  const timeStrToDate = (s) => {
    if (!s) return null;
    if (s instanceof Date) return s;
    const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(s);
    if (!m) return null;
    const d = new Date();
    d.setHours(Number(m[1]), Number(m[2]), Number(m[3] || 0), 0);
    return d;
  };
  out.moving_time = timeStrToDate(out.moving_time);

  // TINYINT (0/1) → boolean for every Checkbox field.
  const intToBool = (v) => v ? true : false;
  out.booked_service_moving_house   = intToBool(out.booked_service_moving_house);
  out.booked_service_moving_office  = intToBool(out.booked_service_moving_office);
  out.booked_service_install_tv     = intToBool(out.booked_service_install_tv);
  out.booked_service_install_bed    = intToBool(out.booked_service_install_bed);
  out.booked_service_packaging      = intToBool(out.booked_service_packaging);
  out.booked_service_clearance      = intToBool(out.booked_service_clearance);
  out.provide_package_material      = intToBool(out.provide_package_material);
  out.extra_service_packaging       = intToBool(out.extra_service_packaging);
  out.extra_service_clearance       = intToBool(out.extra_service_clearance);
  out.extra_service_install_tv      = intToBool(out.extra_service_install_tv);
  out.extra_service_install_bed     = intToBool(out.extra_service_install_bed);

  // DECIMAL string → number for every InputNumber / numeric field.
  const numOrZero = (v) => (v === '' || v === null || v === undefined ? 0 : Number(v));
  out.moving_amount                  = numOrZero(out.moving_amount);
  out.inout_warehouse_amount         = numOrZero(out.inout_warehouse_amount);
  out.deposit_amount                 = numOrZero(out.deposit_amount);
  out.extra_service_packaging_amount = numOrZero(out.extra_service_packaging_amount);
  out.extra_service_clearance_amount = numOrZero(out.extra_service_clearance_amount);
  out.extra_service_install_tv_amount = numOrZero(out.extra_service_install_tv_amount);
  out.extra_service_install_bed_amount = numOrZero(out.extra_service_install_bed_amount);

  return out;
};

// Default values for a brand-new quotation. `moving_date` and
// `moving_time` default to "now" so staff don't have to retype them
// every time they open the form.
const blankQuotation = () => {
  const now = new Date();
  return {
    id: '',
    company_name: '',
    customer_name: '',
    contact_phone: '',
    title: 1,
    moving_date: now,
    moving_time: now,
    old_address: '',
    old_address_stairs: 0,
    old_address_level_type: 1,
    old_address_platform: 0,
    new_address: '',
    new_address_stairs: 0,
    new_address_level_type: 1,
    new_address_platform: 0,
    signature: '',
    booked_service_moving_house: false,
    booked_service_moving_office: false,
    booked_service_install_tv: false,
    booked_service_install_bed: false,
    booked_service_packaging: false,
    booked_service_clearance: false,
    provide_package_material: false,
    provide_package_material_paperbox: 0,
    provide_package_material_bubblepaper: 0,
    provide_package_material_tape: 0,
    moving_amount: 0,
    inout_warehouse_amount: 0,
    deposit_amount: 0,
    provide_box_date: null,
    payment_date: null,
    moving_items: [],
    extra_service_packaging: false,
    extra_service_packaging_amount: 0,
    extra_service_clearance: false,
    extra_service_clearance_amount: 0,
    extra_service_install_tv: false,
    extra_service_install_tv_amount: 0,
    extra_service_install_bed: false,
    extra_service_install_bed_amount: 0,
    remarks: '',
  };
};

const isDev = import.meta.env.DEV;

const Orderform = ({ setPageParams }) => {
  const toast = useRef(null);
  const signaturePadRef = useRef(null);
  const navigate = useNavigate();
  const { id: routeId } = useParams();

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [showMovingItemsOnly, setShowMovingItemsOnly] = useState(false);
  const [quotation, setQuotation] = useState(blankQuotation);
  const [isSaving, setIsSaving] = useState(false);
  const [loaded, setLoaded] = useState(!routeId);
  const [lookupData, setLookupData] = useState(null);
  const [lookupError, setLookupError] = useState(null);

  useEffect(() => {
    setPageParams({
      alias: 'orderform',
      title: routeId ? '編輯報價' : '新增報價',
    });
    if (routeId) {
      (async () => {
        const response = await fetchApi('/api/v1/quotation/getQuotation', { id: routeId });
        if (response?.state === 1 && response.data?.quotation) {
          const q = normalizeQuotationFromServer(response.data.quotation);
          setQuotation(q);
          setLoaded(true);
        } else {
          toast.current?.show({ severity: 'error', summary: '報價單', detail: '讀取失敗' });
          navigate('/quotation');
        }
      })();
    }
  }, [routeId]);

  // Fetch lookup data (titles / level_types / moving_items) on mount.
  // The form doesn't render until this resolves.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const response = await fetchApi('/api/v1/general/getData');
      if (cancelled) return;
      if (response?.state === 1) {
        setLookupData({
          titles:        response.data.titles        || [],
          level_types:   response.data.level_types   || [],
          moving_items:  response.data.moving_items  || [],
        });
      } else {
        setLookupError('讀取參考資料失敗，請稍後再試。');
        toast.current?.show({ severity: 'error', summary: '系統資料', detail: '讀取參考資料失敗' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const updateQuotation = (field, value) => {
    if (['title', 'old_address_level_type', 'new_address_level_type'].includes(field) && value === null) {
      value = 1;
    }
    setQuotation((q) => ({ ...q, [field]: value }));
  };

  const updateQuotationMovingItems = (itemId, qty) => {
    setQuotation((q) => {
      const existing = q.moving_items.find((it) => it.id === itemId);
      const items = existing
        ? q.moving_items.map((it) => (it.id === itemId ? { ...it, qty } : it))
        : [...q.moving_items, { id: itemId, qty }];
      return { ...q, moving_items: items };
    });
  };

  const normalizeNumbers = (q) => {
    const num = (v) => (v === '' || v === null || v === undefined ? 0 : Number(v));
    return {
      ...q,
      old_address_stairs:               num(q.old_address_stairs),
      old_address_platform:             num(q.old_address_platform),
      new_address_stairs:               num(q.new_address_stairs),
      new_address_platform:             num(q.new_address_platform),
      provide_package_material_paperbox:num(q.provide_package_material_paperbox),
      provide_package_material_bubblepaper: num(q.provide_package_material_bubblepaper),
      provide_package_material_tape:    num(q.provide_package_material_tape),
      moving_amount:                    num(q.moving_amount),
      inout_warehouse_amount:           num(q.inout_warehouse_amount),
      deposit_amount:                   num(q.deposit_amount),
      extra_service_packaging_amount:   num(q.extra_service_packaging_amount),
      extra_service_clearance_amount:   num(q.extra_service_clearance_amount),
      extra_service_install_tv_amount:  num(q.extra_service_install_tv_amount),
      extra_service_install_bed_amount: num(q.extra_service_install_bed_amount),
    };
  };

  const submit = async () => {
    setIsSaving(true);
    const normalized = normalizeNumbers(quotation);
    // Make moving_items dense: include every lookup item with default qty=0.
    // The frontend only pushes an item into the array when the user actually
    // clicks its +/- buttons (see updateQuotationMovingItems), so anything the
    // user never touched is absent. The PDF render expects a complete grid so
    // "qty=0 → show name only" works for every item — see DIRECTIONS.md §6 #18.
    const movingItemLookup = lookupData?.moving_items || [];
    if (movingItemLookup.length > 0) {
      const existingQty = new Map(
        (normalized.moving_items || []).map((it) => [it.id, Number(it.qty) || 0])
      );
      normalized.moving_items = movingItemLookup.map((it) => ({
        id: it.id,
        qty: existingQty.get(it.id) ?? 0,
      }));
    }
    const toBeSubmitted = {
      ...normalized,
      id: normalized.id || '',
      booked_service_clearance:     normalized.booked_service_clearance     ? 1 : 0,
      booked_service_install_bed:   normalized.booked_service_install_bed   ? 1 : 0,
      booked_service_install_tv:    normalized.booked_service_install_tv    ? 1 : 0,
      booked_service_moving_house:  normalized.booked_service_moving_house  ? 1 : 0,
      booked_service_moving_office: normalized.booked_service_moving_office ? 1 : 0,
      booked_service_packaging:     normalized.booked_service_packaging     ? 1 : 0,
      extra_service_clearance:      normalized.extra_service_clearance      ? 1 : 0,
      extra_service_install_bed:    normalized.extra_service_install_bed    ? 1 : 0,
      extra_service_install_tv:     normalized.extra_service_install_tv     ? 1 : 0,
      extra_service_packaging:      normalized.extra_service_packaging      ? 1 : 0,
      provide_package_material:     normalized.provide_package_material     ? 1 : 0,
      moving_items: JSON.stringify(normalized.moving_items),
      moving_date:     normalized.moving_date     ? dateToString(normalized.moving_date)     : '',
      moving_time:     normalized.moving_time     ? dateToTimeString(normalized.moving_time) : '',
      payment_date:    normalized.payment_date    ? dateToString(normalized.payment_date)    : '',
      provide_box_date: normalized.provide_box_date ? dateToString(normalized.provide_box_date) : '',
      signature: signaturePadRef.current?.isEmpty?.()
        ? ''
        : signaturePadRef.current?.toDataURL?.() || '',
    };
    const response = await fetchApi('/api/v1/quotation/saveQuotation', toBeSubmitted);
    setIsSaving(false);
    if (response.state === 1) {
      const savedId = response.data?.quotation?.id || normalized.id;
      toast.current?.show({ severity: 'success', summary: '報價單', detail: '已成功儲存報價單。' });
      if (!normalized.id && savedId) {
        setQuotation(blankQuotation());
        setActiveTabIndex(0);
      } else {
        // Merge local edits with the canonical server response, then
        // run everything through the same type-coercion that the
        // initial GET uses — DB columns come back as DATE / TIME /
        // DECIMAL / TINYINT shapes that don't match what the form
        // expects (Calendar needs Date, Checkbox needs bool, etc.).
        const fromServer = response.data?.quotation || {};
        const merged = normalizeQuotationFromServer({
          ...normalized,
          id: savedId,
          ...fromServer,
        });
        setQuotation(merged);
      }
    } else {
      toast.current?.show({ severity: 'error', summary: '報價單', detail: '儲存失敗，請稍後再試。' });
    }
  };

  const preSubmit = () => {
    confirmDialog({
      message: signaturePadRef.current?.isEmpty?.()
        ? '確認以上資料無誤並提交報價？\n\n（客戶未簽署）'
        : '確認以上資料無誤並提交報價？',
      header: '確定提交',
      icon: 'pi pi-info-circle',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-info',
      accept: () => submit(),
    });
  };

  const downloadPdf = async () => {
    if (!quotation.id) {
      toast.current?.show({ severity: 'warn', summary: '報價單', detail: '請先儲存報價單再下載 PDF。' });
      return;
    }
    // Use fetch + Blob instead of window.open(): window.open() doesn't
    // attach Likkan-API-KEY, so the backend 4xx's with invalid_api_key.
    // fetchDownload also keeps the session cookie via credentials:'include'.
    const result = await fetchDownload(
      `/api/v1/quotation/downloadQuotation?id=${quotation.id}`,
      `quotation_${quotation.id.slice(0, 8)}.pdf`,
    );
    if (!result.ok) {
      toast.current?.show({ severity: 'error', summary: '下載 PDF', detail: result.error || '失敗' });
    }
  };

  if (!loaded) return null;
  if (!lookupData) {
    return (
      <div className="flex flex-column align-items-center justify-content-center" style={{ minHeight: '40vh' }}>
        {lookupError ? (
          <div className="text-center">
            <p>{lookupError}</p>
          </div>
        ) : (
          <>
            <ProgressSpinner style={{ width: 32, height: 32 }} strokeWidth="3" />
            <p className="mt-3">載入中…</p>
          </>
        )}
      </div>
    );
  }
  const { titles: TITLES, level_types: LEVEL_TYPES, moving_items: MOVING_ITEMS } = lookupData;

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Toolbar
        className="likkan-toolbar mb-3"
        pt={{
          start: { className: 'flex align-items-center gap-2' },
          end:   { className: 'flex align-items-center gap-2' },
        }}
        start={<div>填寫新報價單</div>}
        end={
          <>
            {quotation.id && (
              <Button label="下載 PDF" icon="pi pi-file-pdf" severity="success" onClick={downloadPdf} />
            )}
            <Button label={routeId ? '更新報價' : '提交報價'} icon="pi pi-save" onClick={preSubmit} loading={isSaving} />
          </>
        }
      />

      <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)}>

        <TabPanel header="客戶資料">
          <div className="likkan-form-grid">
            <div className="likkan-field">
              <label>公司名稱</label>
              <InputText value={quotation.company_name} onChange={(e) => updateQuotation('company_name', e.target.value)} />
            </div>
          </div>
          <div className="likkan-form-grid likkan-form-grid-3 mt-2">
            <div className="likkan-field" style={{ gridColumn: 'span 1 / span 1' }}>
              <label>客戶姓名</label>
              <InputText value={quotation.customer_name} onChange={(e) => updateQuotation('customer_name', e.target.value)} />
            </div>
            <div className="likkan-field">
              <label>聯絡電話</label>
              <InputText value={quotation.contact_phone} onChange={(e) => updateQuotation('contact_phone', e.target.value)} type="tel" />
            </div>
            <div className="likkan-field">
              <label>&nbsp;</label>
              <SelectButton value={quotation.title} onChange={(e) => updateQuotation('title', e.value)} optionLabel="name" optionValue="id" options={TITLES} />
            </div>
          </div>
          <div className="likkan-form-grid likkan-form-grid-3 mt-2">
            <div className="likkan-field">
              <label>搬運日期</label>
              <Calendar value={quotation.moving_date} onChange={(e) => updateQuotation('moving_date', e.value)} dateFormat="yy-mm-dd" placeholder="年-月-日" />
            </div>
            <div className="likkan-field">
              <label>搬運時間</label>
              <Calendar value={quotation.moving_time} onChange={(e) => updateQuotation('moving_time', e.value)} timeOnly hourFormat="24" />
            </div>
          </div>

          <Panel header="舊地址" className="mt-3">
            <div className="likkan-form-grid">
              <div className="likkan-field">
                <label>舊地址</label>
                <InputText value={quotation.old_address} onChange={(e) => updateQuotation('old_address', e.target.value)} />
              </div>
            </div>
            <div className="likkan-form-grid mt-2">
              <div className="likkan-field">
                <label>樓梯</label>
                <InputText value={quotation.old_address_stairs} onChange={(e) => updateQuotation('old_address_stairs', e.target.value)} onFocus={(e) => e.target.select()} type="number" />
              </div>
              <div className="likkan-field">
                <label>&nbsp;</label>
                <SelectButton value={quotation.old_address_level_type} onChange={(e) => updateQuotation('old_address_level_type', e.value)} optionLabel="name" optionValue="id" options={LEVEL_TYPES} />
              </div>
              <div className="likkan-field">
                <label>平台（米）</label>
                <InputText value={quotation.old_address_platform} onChange={(e) => updateQuotation('old_address_platform', e.target.value)} onFocus={(e) => e.target.select()} type="number" />
              </div>
            </div>
          </Panel>

          <Panel header="新地址" className="mt-3">
            <div className="likkan-form-grid">
              <div className="likkan-field">
                <label>新地址</label>
                <InputText value={quotation.new_address} onChange={(e) => updateQuotation('new_address', e.target.value)} />
              </div>
            </div>
            <div className="likkan-form-grid mt-2">
              <div className="likkan-field">
                <label>樓梯</label>
                <InputText value={quotation.new_address_stairs} onChange={(e) => updateQuotation('new_address_stairs', e.target.value)} onFocus={(e) => e.target.select()} type="number" />
              </div>
              <div className="likkan-field">
                <label>&nbsp;</label>
                <SelectButton value={quotation.new_address_level_type} onChange={(e) => updateQuotation('new_address_level_type', e.value)} optionLabel="name" optionValue="id" options={LEVEL_TYPES} />
              </div>
              <div className="likkan-field">
                <label>平台（米）</label>
                <InputText value={quotation.new_address_platform} onChange={(e) => updateQuotation('new_address_platform', e.target.value)} onFocus={(e) => e.target.select()} type="number" />
              </div>
            </div>
          </Panel>

          {isDev && (
            <div className="mt-2 text-sm text-color-secondary">開發模式提示中…</div>
          )}
        </TabPanel>

        <TabPanel header="搬運詳情">
          <div className="likkan-form-grid mt-2">
            <div className="likkan-field flex flex-row align-items-center">
              <Checkbox inputId="booked_service_moving_house" onChange={(e) => updateQuotation('booked_service_moving_house', e.checked)} checked={quotation.booked_service_moving_house} />
              <label htmlFor="booked_service_moving_house" style={{ marginLeft: 8 }}>搬屋 / 搬傢俬 / 搬雜物</label>
            </div>
            <div className="likkan-field flex flex-row align-items-center">
              <Checkbox inputId="booked_service_moving_office" onChange={(e) => updateQuotation('booked_service_moving_office', e.checked)} checked={quotation.booked_service_moving_office} />
              <label htmlFor="booked_service_moving_office" style={{ marginLeft: 8 }}>搬辦公室</label>
            </div>
          </div>
          <div className="likkan-form-grid mt-2">
            <div className="likkan-field flex flex-row align-items-center">
              <Checkbox inputId="booked_service_install_tv" onChange={(e) => updateQuotation('booked_service_install_tv', e.checked)} checked={quotation.booked_service_install_tv} />
              <label htmlFor="booked_service_install_tv" style={{ marginLeft: 8 }}>拆 / 裝 電視</label>
            </div>
            <div className="likkan-field flex flex-row align-items-center">
              <Checkbox inputId="booked_service_install_bed" onChange={(e) => updateQuotation('booked_service_install_bed', e.checked)} checked={quotation.booked_service_install_bed} />
              <label htmlFor="booked_service_install_bed" style={{ marginLeft: 8 }}>拆 / 裝 床 / 櫃</label>
            </div>
          </div>
          <div className="likkan-form-grid mt-2">
            <div className="likkan-field flex flex-row align-items-center">
              <Checkbox inputId="booked_service_packaging" onChange={(e) => updateQuotation('booked_service_packaging', e.checked)} checked={quotation.booked_service_packaging} />
              <label htmlFor="booked_service_packaging" style={{ marginLeft: 8 }}>包裝入箱 / 拆包裝</label>
            </div>
            <div className="likkan-field flex flex-row align-items-center">
              <Checkbox inputId="booked_service_clearance" onChange={(e) => updateQuotation('booked_service_clearance', e.checked)} checked={quotation.booked_service_clearance} />
              <label htmlFor="booked_service_clearance" style={{ marginLeft: 8 }}>清場垃圾</label>
            </div>
          </div>

          <div className="likkan-form-grid mt-3">
            <div className="likkan-field flex flex-row align-items-center">
              <Checkbox inputId="provide_package_material" onChange={(e) => updateQuotation('provide_package_material', e.checked)} checked={quotation.provide_package_material} />
              <label htmlFor="provide_package_material" style={{ marginLeft: 8 }}>供應包裝物料</label>
            </div>
            <div className="likkan-field">
              <label>紙箱</label>
              <InputText value={quotation.provide_package_material_paperbox} onChange={(e) => updateQuotation('provide_package_material_paperbox', e.target.value)} onFocus={(e) => e.target.select()} type="number" />
            </div>
            <div className="likkan-field">
              <label>泡泡紙</label>
              <InputText value={quotation.provide_package_material_bubblepaper} onChange={(e) => updateQuotation('provide_package_material_bubblepaper', e.target.value)} onFocus={(e) => e.target.select()} type="number" />
            </div>
            <div className="likkan-field">
              <label>膠紙</label>
              <InputText value={quotation.provide_package_material_tape} onChange={(e) => updateQuotation('provide_package_material_tape', e.target.value)} onFocus={(e) => e.target.select()} type="number" />
            </div>
          </div>
        </TabPanel>

        <TabPanel header="搬運項目">
          <div className="flex justify-content-end mb-2">
            <Checkbox inputId="showMovingItemsOnly" onChange={(e) => setShowMovingItemsOnly(e.checked)} checked={showMovingItemsOnly} />
            <label htmlFor="showMovingItemsOnly" style={{ marginLeft: 8 }}>只顯示已選項目</label>
          </div>
          <div className="likkan-form-grid likkan-form-grid-3">
            {MOVING_ITEMS
              .filter((it) => !showMovingItemsOnly || quotation.moving_items.some((m) => m.id === it.id && m.qty > 0))
              .map((item) => {
                const existing = quotation.moving_items.find((m) => m.id === item.id);
                return (
                  <div key={item.id} className="likkan-moving-item-row">
                    <span>{item.name}</span>
                    <ItemNumberInput
                      value={existing?.qty || 0}
                      onChange={(qty) => updateQuotationMovingItems(item.id, qty)}
                    />
                  </div>
                );
              })}
          </div>
        </TabPanel>

        <TabPanel header="收費">
          <div className="likkan-form-grid likkan-form-grid-3">
            <div className="likkan-field">
              <label>搬運費</label>
              <InputText value={quotation.moving_amount} onChange={(e) => updateQuotation('moving_amount', e.target.value)} onFocus={(e) => e.target.select()} type="number" />
            </div>
            <div className="likkan-field">
              <label>出入倉費</label>
              <InputText value={quotation.inout_warehouse_amount} onChange={(e) => updateQuotation('inout_warehouse_amount', e.target.value)} onFocus={(e) => e.target.select()} type="number" />
            </div>
            <div className="likkan-field">
              <label>訂金</label>
              <InputText value={quotation.deposit_amount} onChange={(e) => updateQuotation('deposit_amount', e.target.value)} onFocus={(e) => e.target.select()} type="number" />
            </div>
          </div>
          <div className="likkan-form-grid likkan-form-grid-3 mt-2">
            <div className="likkan-field">
              <label>提供物資日期</label>
              <Calendar value={quotation.provide_box_date} onChange={(e) => updateQuotation('provide_box_date', e.value)} dateFormat="yy-mm-dd" />
            </div>
            <div className="likkan-field">
              <label>付款日期</label>
              <Calendar value={quotation.payment_date} onChange={(e) => updateQuotation('payment_date', e.value)} dateFormat="yy-mm-dd" />
            </div>
          </div>

          <div className="mt-3 font-bold">附加服務</div>
          <div className="likkan-form-grid mt-2">
            <div className="likkan-field flex flex-row align-items-center">
              <Checkbox inputId="extra_service_packaging" onChange={(e) => updateQuotation('extra_service_packaging', e.checked)} checked={quotation.extra_service_packaging} />
              <label htmlFor="extra_service_packaging" style={{ marginLeft: 8 }}>包裝入箱服務</label>
              <InputText value={quotation.extra_service_packaging_amount} onChange={(e) => updateQuotation('extra_service_packaging_amount', e.target.value)} onFocus={(e) => e.target.select()} type="number" style={{ width: 120, marginLeft: 8 }} />
            </div>
            <div className="likkan-field flex flex-row align-items-center">
              <Checkbox inputId="extra_service_clearance" onChange={(e) => updateQuotation('extra_service_clearance', e.checked)} checked={quotation.extra_service_clearance} />
              <label htmlFor="extra_service_clearance" style={{ marginLeft: 8 }}>清場垃圾服務</label>
              <InputText value={quotation.extra_service_clearance_amount} onChange={(e) => updateQuotation('extra_service_clearance_amount', e.target.value)} onFocus={(e) => e.target.select()} type="number" style={{ width: 120, marginLeft: 8 }} />
            </div>
            <div className="likkan-field flex flex-row align-items-center">
              <Checkbox inputId="extra_service_install_tv" onChange={(e) => updateQuotation('extra_service_install_tv', e.checked)} checked={quotation.extra_service_install_tv} />
              <label htmlFor="extra_service_install_tv" style={{ marginLeft: 8 }}>拆裝電視服務</label>
              <InputText value={quotation.extra_service_install_tv_amount} onChange={(e) => updateQuotation('extra_service_install_tv_amount', e.target.value)} onFocus={(e) => e.target.select()} type="number" style={{ width: 120, marginLeft: 8 }} />
            </div>
            <div className="likkan-field flex flex-row align-items-center">
              <Checkbox inputId="extra_service_install_bed" onChange={(e) => updateQuotation('extra_service_install_bed', e.checked)} checked={quotation.extra_service_install_bed} />
              <label htmlFor="extra_service_install_bed" style={{ marginLeft: 8 }}>拆裝床櫃服務</label>
              <InputText value={quotation.extra_service_install_bed_amount} onChange={(e) => updateQuotation('extra_service_install_bed_amount', e.target.value)} onFocus={(e) => e.target.select()} type="number" style={{ width: 120, marginLeft: 8 }} />
            </div>
          </div>

          <div className="likkan-field mt-3">
            <label>備註</label>
            <InputTextarea value={quotation.remarks} onChange={(e) => updateQuotation('remarks', e.target.value)} rows={5} autoResize />
          </div>
        </TabPanel>

        <TabPanel header="客戶簽署">
          <div className="likkan-signature-wrap">
            <div className="likkan-signature-frame">
              <SignaturePad ref={signaturePadRef} />
            </div>
            <div className="likkan-row-actions">
              <Button label="清除簽名" icon="pi pi-eraser" outlined onClick={() => signaturePadRef.current?.clear?.()} />
            </div>
          </div>
        </TabPanel>

      </TabView>

      <ConfirmDialog />
    </>
  );
};

export default Orderform;
